import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import {
  getApexData,
  getApexAccountAllocation,
  getApexChartData,
  getHoldingsData,
  getApexDeposits,
  getApexWithdrawals,
  getFirmDataAccountTransactions,
  getFirmDataAccountHoldings,
  TimeRange,
  getApexAssetMapData,
  getAccountFinancialDetailData,
  getAccountDetailData,
} from '@/api/custom';
import { AccountType, ASSET_LIABILITY } from '@/util/planning-util';
import { resolveFirmDataEnabled } from '@/hooks/useFirmDataEnabled';
import { toTransferList } from '@/util/firm-data-transfers';
import { toApexHoldings } from '@/util/firm-data-holdings';

export const APEX_STALE_USER_REQUEST = 'stale-user-request';

const getActiveUserIdFromState = (state: RootState): string | null =>
  state.userinfo.user?.userId || null;

const getFirmDataEnabledFromState = (state: RootState): Promise<boolean> =>
  resolveFirmDataEnabled(state.userinfo.user?.client_id);

const isStaleForStartingUser = (getState: () => unknown, startingUserId: string | null): boolean =>
  getActiveUserIdFromState(getState() as RootState) !== startingUserId;

/**
 * Translate whichever account id a caller holds into firm-data's own account
 * id, which is what firm-data-backed endpoints filter on.
 *
 * Two id spaces are in play for the same account: firm-data's `account_id`
 * and the custodian's `custodian_account_id` (see useCustodianAccounts, whose
 * ids are Apex's and are meant for fo-apex-backend endpoints). Callers may
 * legitimately hold either, so resolve by matching on both rather than
 * assuming. An id that matches neither is passed through untouched — the
 * request then simply returns nothing for it, instead of silently returning
 * another account's transfers.
 */
const firmDataAccountId = (state: RootState, accountId: string): string => {
  // Firm-data's own id wins. Matching both fields in one pass would let an
  // account whose custodian id happens to equal another account's firm-data id
  // answer for it, decided by nothing more than array order.
  const directMatch = state.plaid.accounts.find((account) => account.account_id === accountId);
  if (directMatch) {
    return directMatch.account_id;
  }

  const custodianMatch = state.plaid.accounts.find(
    (account) => account.custodian_account_id === accountId,
  );
  return custodianMatch?.account_id ?? accountId;
};

// async thunk：get apex accounts
export const fetchApexAccounts = createAsyncThunk(
  'apex/fetchAccounts',
  async (_: void, { getState, rejectWithValue }) => {
    const startingUserId = getActiveUserIdFromState(getState() as RootState);
    let response;

    try {
      const firmDataEnabled = await getFirmDataEnabledFromState(getState() as RootState);
      if (isStaleForStartingUser(getState, startingUserId)) {
        return rejectWithValue(APEX_STALE_USER_REQUEST);
      }
      response = await getApexData(firmDataEnabled);
    } catch (error) {
      if (isStaleForStartingUser(getState, startingUserId)) {
        return rejectWithValue(APEX_STALE_USER_REQUEST);
      }
      throw error;
    }

    if (isStaleForStartingUser(getState, startingUserId)) {
      return rejectWithValue(APEX_STALE_USER_REQUEST);
    }

    const accountDetailsPromises = response.accounts.map((account) =>
      getAccountDetailData(account.account_id),
    );
    const accountDetailsSettled = await Promise.allSettled(accountDetailsPromises);

    const accountDetails = accountDetailsSettled.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value.account_detail;
      }
      const accountId = response.accounts[index]?.account_id ?? 'unknown';
      console.error(`Failed to fetch Apex account detail for account_id=${accountId}`);
      return null;
    });

    if (isStaleForStartingUser(getState, startingUserId)) {
      return rejectWithValue(APEX_STALE_USER_REQUEST);
    }

    response.accounts.forEach((account, index) => {
      account.account_detail = accountDetails[index] ?? undefined;
    });

    return response.accounts.map((account) => ({
      id: account.account_id,
      name: account.account_number,
      type: account.account_investment_type as AccountType,
      total_equity: parseFloat(account.total_equity || '0'),
      asset_or_liability: ASSET_LIABILITY.ASSET,
      account_detail: account.account_detail,
    }));
  },
);

export const fetchApexAssetMap = createAsyncThunk(
  'apex/fetchAssetMap',
  async (_: void, { getState, rejectWithValue }) => {
    const startingUserId = getActiveUserIdFromState(getState() as RootState);
    let response;

    try {
      response = await getApexAssetMapData();
    } catch (error) {
      if (isStaleForStartingUser(getState, startingUserId)) {
        return rejectWithValue(APEX_STALE_USER_REQUEST);
      }
      throw error;
    }

    if (isStaleForStartingUser(getState, startingUserId)) {
      return rejectWithValue(APEX_STALE_USER_REQUEST);
    }

    return response;
  },
);

// async thunk：get account allocation information
export const fetchAccountAllocation = createAsyncThunk(
  'apex/fetchAllocation',
  async (
    {
      accountId,
      activeUserId,
    }: {
      accountId: string;
      activeUserId: string;
    },
    { getState, rejectWithValue },
  ) => {
    const startingUserId = getActiveUserIdFromState(getState() as RootState);
    if (startingUserId !== activeUserId) {
      return rejectWithValue(APEX_STALE_USER_REQUEST);
    }
    let response;

    try {
      response = await getApexAccountAllocation(accountId);
    } catch (error) {
      if (isStaleForStartingUser(getState, startingUserId)) {
        return rejectWithValue(APEX_STALE_USER_REQUEST);
      }
      throw error;
    }

    if (isStaleForStartingUser(getState, startingUserId)) {
      return rejectWithValue(APEX_STALE_USER_REQUEST);
    }

    return { accountId, data: response, activeUserId: startingUserId };
  },
);

// async thunk：get chart data
export const fetchApexChartData = createAsyncThunk(
  'apex/fetchApexChartData',
  async (
    {
      accountIds,
      range = 'ALL',
      activeUserId,
    }: {
      accountIds: string[];
      range?: TimeRange;
      activeUserId: string;
    },
    { getState, rejectWithValue },
  ) => {
    const startingUserId = getActiveUserIdFromState(getState() as RootState);
    if (startingUserId !== activeUserId) {
      return rejectWithValue(APEX_STALE_USER_REQUEST);
    }
    let response;

    try {
      response = await getApexChartData(accountIds, range);
    } catch (error) {
      if (isStaleForStartingUser(getState, startingUserId)) {
        return rejectWithValue(APEX_STALE_USER_REQUEST);
      }
      throw error;
    }

    if (isStaleForStartingUser(getState, startingUserId)) {
      return rejectWithValue(APEX_STALE_USER_REQUEST);
    }

    return { chartData: response, activeUserId: startingUserId };
  },
);

// async thunk: get account details
// export const fetchAccountDetails = createAsyncThunk(
//   "apex/fetchAccountDetails",
//   async (accountId: string) => {
//     const response = await getAccountDetailData(accountId);
//     return { accountId, data: response.account_detail };
//   },
// );

// async thunk：get account financial details
export const fetchAccountFinancialDetails = createAsyncThunk(
  'apex/fetchFinancialDetails',
  async (accountId: string, { getState, rejectWithValue }) => {
    const startingUserId = getActiveUserIdFromState(getState() as RootState);
    let response;

    try {
      response = await getAccountFinancialDetailData(accountId);
    } catch (error) {
      if (isStaleForStartingUser(getState, startingUserId)) {
        return rejectWithValue(APEX_STALE_USER_REQUEST);
      }
      throw error;
    }

    if (isStaleForStartingUser(getState, startingUserId)) {
      return rejectWithValue(APEX_STALE_USER_REQUEST);
    }

    return { accountId, data: response };
  },
);

// async thunk：get account holdings
export const fetchAccountHoldings = createAsyncThunk(
  'apex/fetchHoldings',
  async (accountId: string, { getState, rejectWithValue }) => {
    const startingUserId = getActiveUserIdFromState(getState() as RootState);

    // A firm-data firm reads holdings from firm-data in one call instead of
    // the per-account Apex call below. Legacy firms are untouched: same
    // Apex holdings call, same payload as before.
    let firmDataEnabled;
    try {
      firmDataEnabled = await getFirmDataEnabledFromState(getState() as RootState);
    } catch (error) {
      if (isStaleForStartingUser(getState, startingUserId)) {
        return rejectWithValue(APEX_STALE_USER_REQUEST);
      }
      throw error;
    }

    if (isStaleForStartingUser(getState, startingUserId)) {
      return rejectWithValue(APEX_STALE_USER_REQUEST);
    }

    if (firmDataEnabled) {
      let holdings;
      try {
        holdings = await getFirmDataAccountHoldings(
          firmDataAccountId(getState() as RootState, accountId),
        );
      } catch (error) {
        if (isStaleForStartingUser(getState, startingUserId)) {
          return rejectWithValue(APEX_STALE_USER_REQUEST);
        }
        throw error;
      }

      if (isStaleForStartingUser(getState, startingUserId)) {
        return rejectWithValue(APEX_STALE_USER_REQUEST);
      }

      return { accountId, data: { holdings: toApexHoldings(holdings) } };
    }

    let response;
    try {
      response = await getHoldingsData(accountId);
    } catch (error) {
      if (isStaleForStartingUser(getState, startingUserId)) {
        return rejectWithValue(APEX_STALE_USER_REQUEST);
      }
      throw error;
    }

    if (isStaleForStartingUser(getState, startingUserId)) {
      return rejectWithValue(APEX_STALE_USER_REQUEST);
    }

    return { accountId, data: response };
  },
);

// async thunk：get account transfers
export const fetchAccountTransfers = createAsyncThunk(
  'apex/fetchTransfers',
  async (accountId: string, { getState, rejectWithValue }) => {
    const startingUserId = getActiveUserIdFromState(getState() as RootState);
    let deposits;

    // A firm-data firm reads transfer history from firm-data in one call
    // instead of the two per-account Apex calls below. Legacy firms are
    // untouched: same two calls, same order, same payloads as before.
    let firmDataEnabled;
    try {
      firmDataEnabled = await getFirmDataEnabledFromState(getState() as RootState);
    } catch (error) {
      if (isStaleForStartingUser(getState, startingUserId)) {
        return rejectWithValue(APEX_STALE_USER_REQUEST);
      }
      throw error;
    }

    if (isStaleForStartingUser(getState, startingUserId)) {
      return rejectWithValue(APEX_STALE_USER_REQUEST);
    }

    if (firmDataEnabled) {
      let transactions;
      try {
        transactions = await getFirmDataAccountTransactions(
          firmDataAccountId(getState() as RootState, accountId),
        );
      } catch (error) {
        if (isStaleForStartingUser(getState, startingUserId)) {
          return rejectWithValue(APEX_STALE_USER_REQUEST);
        }
        throw error;
      }

      if (isStaleForStartingUser(getState, startingUserId)) {
        return rejectWithValue(APEX_STALE_USER_REQUEST);
      }

      return { accountId, data: toTransferList(transactions) };
    }

    try {
      deposits = await getApexDeposits(accountId);
    } catch (error) {
      if (isStaleForStartingUser(getState, startingUserId)) {
        return rejectWithValue(APEX_STALE_USER_REQUEST);
      }
      throw error;
    }

    if (isStaleForStartingUser(getState, startingUserId)) {
      return rejectWithValue(APEX_STALE_USER_REQUEST);
    }

    let withdrawals;
    try {
      withdrawals = await getApexWithdrawals(accountId);
    } catch (error) {
      if (isStaleForStartingUser(getState, startingUserId)) {
        return rejectWithValue(APEX_STALE_USER_REQUEST);
      }
      throw error;
    }

    if (isStaleForStartingUser(getState, startingUserId)) {
      return rejectWithValue(APEX_STALE_USER_REQUEST);
    }

    return { accountId, data: { deposits, withdrawals } };
  },
);
